import { Component, EventEmitter, Input, OnInit, Output, HostListener } from '@angular/core';
import { AppComponent } from 'src/app/app.component';
import { IoService } from 'src/app/services/io/io.service';
import { ClientSettingsService } from 'src/app/services/client-settings/client-settings.service';

/**
 * Bulk Add wizard — consolidates every bulk item-add path on a transfer into
 * one modal with three branches:
 *   Import Data  — upload a CSV/Excel file, map its columns (or have a saved
 *                  client-wide format recognized automatically), preview the
 *                  matched lines, and commit.
 *   Replenish    — fill-to-stock-target (par levels) or sales-based days of
 *                  supply; editable suggestion preview before commit.
 *   Add by Filter — the old "Add All" by category/group.
 *
 * All computation happens server-side (importPreview/importRemap/importCommit,
 * getReplenishSuggestions/commitReplenishItems, addAllInventory); the wizard
 * only orchestrates and lets the user adjust before committing.
 */
@Component({
  selector: 'app-bulk-add',
  templateUrl: './bulk-add.component.html',
  styleUrls: ['./bulk-add.component.scss']
})
export class BulkAddComponent implements OnInit {

  @Input() purchase: any;
  @Input() categories: any;
  @Output() closed = new EventEmitter<any>();

  step = 'mode';
  mode = null;
  busy = false;
  busyMessage = '';

  // Import state
  importId = null;
  headers = [];
  sampleRows = [];
  rowCount = 0;
  format: any = null;
  mapping: any = this.emptyMapping();
  preview: any = null;
  saveFormatName = '';
  createUnmatched = false;

  // Replenish state
  strategy = 'days-of-supply';
  stockTargetsEnabled = false;
  windowDays: number;
  targetDays: number;
  suggestions: any = null;
  replenishMeta: any = null;
  // Category/subcategory limiter — same drill-down picker and scope entry
  // shape ([{type: 'categoryId'|'subcategoryId', _id, name}]) as the
  // discount wizard's campaign scope.
  scopeCategories: any[] = null;
  activeScopeCategory: any = null;
  replenishScope: any[] = [];

  // Filter state
  selectedCategory: any = null;

  constructor(
    private ioService: IoService,
    private clientSettingsService: ClientSettingsService,
  ) { }

  ngOnInit(): void {
    this.windowDays = this.clientSettingsService.getReplenishWindowDays();
    this.targetDays = this.clientSettingsService.getReplenishTargetDays();
    this.stockTargetsEnabled = this.clientSettingsService.isReplenishStockTargetsEnabled();

    if (this.categories && this.categories.length > 0) {
      this.selectedCategory = this.categories[0];
    }

    const branches = this.availableBranches();
    if (branches.length === 1) {
      this.selectMode(branches[0]);
    }
  }

  emptyMapping() {
    return {
      identityColumns: [],
      qtyColumn: null,
      qtySemantics: 'target',
      costColumn: null,
      costBasis: 'unit',
    };
  }

  // ==== Branch availability ==========================================

  canImport() {
    return !!(this.purchase.authorization?.upload && this.purchase.authorization?.update);
  }

  canReplenish() {
    return !!this.purchase.inventoryReplenishment;
  }

  canFilter() {
    return !!this.purchase.typeObject?.allowAddAll;
  }

  availableBranches() {
    const branches = [];
    if (this.canImport()) { branches.push('import'); }
    if (this.canReplenish()) { branches.push('replenish'); }
    if (this.canFilter()) { branches.push('filter'); }
    return branches;
  }

  selectMode(mode) {
    this.mode = mode;
    if (mode === 'import') {
      this.step = 'upload';
    } else if (mode === 'replenish') {
      this.step = 'replenishParams';
      if (this.scopeCategories === null) {
        this.getScopeOptions();
      }
    } else {
      this.step = 'filter';
    }
  }

  back() {
    switch (this.step) {
      case 'mapping':
      case 'preview':
        this.step = 'upload';
        break;
      case 'replenishPreview':
        this.step = 'replenishParams';
        break;
      default:
        this.step = 'mode';
        this.mode = null;
    }
  }

  close() {
    this.closed.emit({ reload: false });
  }

  // Successful commits close the wizard and announce the outcome as a toast —
  // no acknowledgment step. Failures toast too but keep the wizard open on the
  // current step so the user can adjust and retry.
  finish(summary: string) {
    AppComponent.showSnack(summary, 'OK', 5000);
    this.closed.emit({ reload: true });
  }

  toastError(message: string) {
    AppComponent.showSnack(message, 'OK', 5000);
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  stepTitle() {
    switch (this.step) {
      case 'mode': return 'Bulk Add';
      case 'upload': return 'Bulk Add › Import';
      case 'mapping': return 'Bulk Add › Import › Map Columns';
      case 'preview': return 'Bulk Add › Import › Preview';
      case 'replenishParams': return 'Bulk Add › Replenish';
      case 'replenishPreview': return 'Bulk Add › Replenish › Preview';
      case 'filter': return 'Bulk Add › Add by Filter';
    }
    return 'Bulk Add';
  }

  // ==== Import branch ================================================

  onFileChange(event) {
    const file = event.target.files && event.target.files[0];
    // Reset so reselecting the same file fires change again.
    event.target.value = '';
    if (!file) {
      return;
    }

    this.busy = true;
    this.busyMessage = 'Uploading ' + file.name + '…';
    this.ioService.upload('transfer/importPreview', {
      purchaseId: this.purchase._id,
    }, file).subscribe((uploadEvent: any) => {
      if (uploadEvent.type == 4) {
        this.busy = false;
        if (uploadEvent.body?.importId) {
          this.handlePreviewResponse(uploadEvent.body);
        } else {
          this.toastError(uploadEvent.body?.msg || 'Upload failed.');
        }
      }
    }, (err) => {
      this.busy = false;
      this.toastError(err?.error?.msg || 'Upload failed.');
    });
  }

  handlePreviewResponse(body) {
    this.importId = body.importId;
    this.headers = body.headers || [];
    this.sampleRows = body.sampleRows || [];
    this.rowCount = body.rowCount || 0;
    this.format = body.format;
    this.saveFormatName = '';
    this.createUnmatched = false;

    if (body.format?.mapping) {
      this.mapping = { ...this.emptyMapping(), ...body.format.mapping };
    } else if (body.suggestedMapping) {
      this.mapping = { ...this.emptyMapping(), ...body.suggestedMapping };
      if (body.defaultQtySemantics) {
        this.mapping.qtySemantics = body.defaultQtySemantics;
      }
    } else {
      this.mapping = this.emptyMapping();
    }

    if (body.preview) {
      this.preview = body.preview;
      this.step = 'preview';
    } else {
      this.step = 'mapping';
    }
  }

  headerRole(header) {
    if (this.mapping.identityColumns.indexOf(header) !== -1) { return 'identity'; }
    if (this.mapping.qtyColumn === header) { return 'qty'; }
    if (this.mapping.costColumn === header) { return 'cost'; }
    return 'ignore';
  }

  setRole(header, role) {
    // Clear any current role first
    const identityIndex = this.mapping.identityColumns.indexOf(header);
    if (identityIndex !== -1) { this.mapping.identityColumns.splice(identityIndex, 1); }
    if (this.mapping.qtyColumn === header) { this.mapping.qtyColumn = null; }
    if (this.mapping.costColumn === header) { this.mapping.costColumn = null; }

    switch (role) {
      case 'identity':
        this.mapping.identityColumns.push(header);
        break;
      case 'qty':
        this.mapping.qtyColumn = header;
        break;
      case 'cost':
        this.mapping.costColumn = header;
        break;
    }
  }

  moveIdentity(header, direction) {
    const columns = this.mapping.identityColumns;
    const from = columns.indexOf(header);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= columns.length) {
      return;
    }
    columns.splice(from, 1);
    columns.splice(to, 0, header);
  }

  sampleValues(header) {
    return this.sampleRows.slice(0, 3).map((row) => row[header]).filter((value) => value !== '' && value != null);
  }

  mappingComplete() {
    return this.mapping.identityColumns.length > 0 && !!this.mapping.qtyColumn;
  }

  async applyMapping() {
    if (!this.mappingComplete()) {
      return;
    }
    this.busy = true;
    this.busyMessage = 'Matching rows…';
    try {
      this.preview = await this.ioService.post('/transfer/importRemap', {
        purchaseId: this.purchase._id,
        importId: this.importId,
        mapping: this.mapping,
      });
      this.step = 'preview';
    } catch (e) {
      this.toastError((e as any)?.error?.msg || 'Preview failed.');
    }
    this.busy = false;
  }

  editMapping() {
    this.step = 'mapping';
  }

  // Guard rail (CloudAPI 1.2609.89): the server flags reprices that move a
  // vendor product's price by more than 3x as `suspect` and skips them at
  // commit unless their ids are sent back in confirmedReprices.
  suspectPriceChanges(): any[] {
    return (this.preview?.priceChanges || []).filter(change => change.suspect);
  }

  priceChangeFactor(change): string {
    if (!(change.oldPrice > 0) || !(change.newPrice > 0)) return '';
    const factor = change.newPrice > change.oldPrice ? change.newPrice / change.oldPrice : change.oldPrice / change.newPrice;
    return factor.toFixed(factor >= 10 ? 0 : 1) + '× ' + (change.newPrice > change.oldPrice ? 'higher' : 'lower');
  }

  isVendorInvoice() {
    return this.format?.type === 'vendor-invoice';
  }

  async commitImport() {
    this.busy = true;
    this.busyMessage = 'Importing…';
    try {
      const commitInput: any = {
        purchaseId: this.purchase._id,
        importId: this.importId,
        mapping: this.mapping,
      };
      commitInput.confirmedReprices = this.suspectPriceChanges()
        .filter(change => change.confirm && change.accountId)
        .map(change => String(change.accountId));
      if (this.isVendorInvoice()) {
        commitInput.format = 'vendor-invoice';
        commitInput.createUnmatched = this.createUnmatched;
      } else if (this.saveFormatName.trim()) {
        commitInput.saveFormat = {
          name: this.saveFormatName.trim(),
          formatId: this.format?.type === 'saved' ? this.format.formatId : undefined,
        };
      }
      const commitResult: any = await this.ioService.post('/transfer/importCommit', commitInput);
      this.busy = false;

      let summary = 'Imported ' + (commitResult.itemCount || 0) + ' line' + (commitResult.itemCount === 1 ? '' : 's');
      if (commitResult.repricedCount) {
        summary += ' · ' + commitResult.repricedCount + ' vendor products repriced';
      }
      if (commitResult.skippedRepriceCount) {
        summary += ' · ' + commitResult.skippedRepriceCount + ' price change' + (commitResult.skippedRepriceCount === 1 ? '' : 's')
          + ' skipped (not confirmed)';
      }
      if (commitResult.createdCount) {
        summary += ' · ' + commitResult.createdCount + ' vendor products created';
      }
      if (commitResult.savedFormat) {
        summary += ' · format “' + commitResult.savedFormat.name + '” saved';
      }
      summary += '.';
      this.finish(summary);
    } catch (e) {
      this.busy = false;
      this.toastError((e as any)?.error?.msg || 'Import failed.');
    }
  }

  // ==== Replenish branch =============================================

  // Categories and subcategories are both inventory Groups with subtype
  // 'category'; a subcategory carries a parentId. Built into a category tree
  // for the drill-down limiter (mirrors the discount wizard's scope picker).
  async getScopeOptions() {
    try {
      const groups: any = await this.ioService.post('/group/getGroups', {
        type: 'inventory',
        subtype: 'category',
        anyLocation: true,
        anyParent: true,
      });
      const subcategories = (groups || []).filter(group => group.parentId);
      this.scopeCategories = (groups || []).filter(group => !group.parentId).map(category => {
        category.subcategories = subcategories.filter(sub => sub.parentId === category._id);
        return category;
      });
    } catch (e) {
      this.scopeCategories = [];
    }
  }

  activateScopeCategory(category) {
    this.activeScopeCategory = category;
  }

  isScopeSelected(id) {
    return this.replenishScope.some(entry => entry._id === id);
  }

  toggleScopeCategory(category) {
    const index = this.replenishScope.findIndex(entry => entry._id === category._id);
    if (index > -1) {
      this.replenishScope.splice(index, 1);
    } else {
      // The whole category includes its subcategories; drop now-redundant ones.
      this.replenishScope = this.replenishScope.filter(entry =>
        !category.subcategories?.some(sub => sub._id === entry._id));
      this.replenishScope.push({
        type: 'categoryId',
        _id: category._id,
        name: category.name,
      });
    }
  }

  toggleScopeSubcategory(subcategory, parent) {
    if (this.isScopeSelected(parent._id)) {
      // Already included via the whole category.
      return;
    }
    const index = this.replenishScope.findIndex(entry => entry._id === subcategory._id);
    if (index > -1) {
      this.replenishScope.splice(index, 1);
    } else {
      this.replenishScope.push({
        type: 'subcategoryId',
        _id: subcategory._id,
        name: parent.name + ' > ' + subcategory.name,
      });
    }
  }

  removeScope(entry) {
    this.replenishScope.splice(this.replenishScope.indexOf(entry), 1);
  }

  clearScope() {
    this.replenishScope = [];
  }

  async runSuggestions() {
    this.busy = true;
    this.busyMessage = this.strategy === 'days-of-supply'
      ? 'Analyzing ' + this.windowDays + ' days of sales…'
      : 'Comparing stock targets…';
    try {
      const suggestionsInput: any = {
        purchaseId: this.purchase._id,
        strategy: this.strategy,
        windowDays: this.windowDays,
        targetDays: this.targetDays,
      };
      if (this.replenishScope.length > 0) {
        suggestionsInput.scope = this.replenishScope.map(entry => ({
          type: entry.type,
          _id: entry._id,
          name: entry.name,
        }));
      }
      const response: any = await this.ioService.post('/transfer/getReplenishSuggestions', suggestionsInput);
      this.replenishMeta = response.meta;
      this.suggestions = (response.suggestions || []).map((suggestion) => ({
        ...suggestion,
        itemNumber: Array.isArray(suggestion.barcode)
          ? (suggestion.barcode.length > 0 ? suggestion.barcode[0] : null)
          : suggestion.barcode,
        include: !suggestion.flags?.notCarriedAtTarget,
        qty: suggestion.suggestedQty,
      }));
      this.suggestionFilter = '';
      this.suggestionSortKey = null;
      this.suggestionSortDirection = 1;
      this.step = 'replenishPreview';
    } catch (e) {
      this.toastError((e as any)?.error?.msg || 'Could not compute suggestions.');
    }
    this.busy = false;
  }

  // ---- Preview table filter + sort (client-side over the loaded suggestions) ----
  suggestionFilter = '';
  suggestionSortKey: string = null;
  suggestionSortDirection = 1;

  // Item #-primary display (client setting): the suggestion cell's small
  // identifier line shows Item # instead of SKU.
  itemNumberPrimary(): boolean {
    return this.clientSettingsService.isItemNumberPrimary();
  }

  // Whether any loaded suggestion carries a source on-hand / case pack —
  // header columns key off the whole set, not (fragilely) the first row.
  hasSourceColumn(): boolean {
    return (this.suggestions || []).some((suggestion) => suggestion.onHandSource !== null);
  }

  hasCaseColumn(): boolean {
    return (this.suggestions || []).some((suggestion) => suggestion.caseQty > 1);
  }

  // The suggestions' quantity column is named after the transfer's own qty
  // column for the current step (e.g. "Request Qty" on Warehouse to Store),
  // so the wizard and the grid behind it agree.
  qtyLabel(): string {
    const typeObject = this.purchase?.typeObject;
    const status = this.purchase?.status;
    let fields: any[] = [];
    if (typeObject && Array.isArray(typeObject.steps)) {
      const step = typeObject.steps.find((candidate) => candidate.key === status);
      fields = (step && step.fields) || [];
    } else if (typeObject && typeObject.fields) {
      fields = typeObject.fields[status] || typeObject.fields['open'] || [];
    }
    const qtyField = fields.find((field) => field && field.key === 'qty');
    return (qtyField && (qtyField.name || qtyField.label)) || 'Order Qty';
  }

  sortSuggestions(key: string) {
    if (this.suggestionSortKey === key) {
      if (this.suggestionSortDirection === 1) {
        this.suggestionSortDirection = -1;
      } else {
        this.suggestionSortKey = null;
        this.suggestionSortDirection = 1;
      }
    } else {
      this.suggestionSortKey = key;
      this.suggestionSortDirection = 1;
    }
  }

  // Memoized: the template calls this every change-detection pass; returning a
  // fresh array while filtered/sorted makes ngFor rebuild the rows each pass,
  // which can swallow clicks (see transfer.component searchResultFields).
  private displayCache: any[] = null;
  private displayCacheKey: string = null;

  displaySuggestions() {
    const cacheKey = [
      this.suggestions ? this.suggestions.length : -1,
      this.suggestionFilter, this.suggestionSortKey, this.suggestionSortDirection,
    ].join('|');
    if (this.displayCache && this.displayCacheKey === cacheKey) {
      return this.displayCache;
    }
    const result = this.computeDisplaySuggestions();
    this.displayCache = result;
    this.displayCacheKey = cacheKey;
    return result;
  }

  private computeDisplaySuggestions() {
    let rows = this.suggestions || [];

    const filter = (this.suggestionFilter || '').trim().toLowerCase();
    if (filter) {
      rows = rows.filter((suggestion) => {
        const barcodes = Array.isArray(suggestion.barcode) ? suggestion.barcode : [suggestion.barcode];
        return [suggestion.name, suggestion.sku, suggestion.attr, suggestion.categoryName, suggestion.subcategoryName]
          .concat(barcodes)
          .some((value) => value && String(value).toLowerCase().indexOf(filter) !== -1);
      });
    }

    if (this.suggestionSortKey) {
      const key = this.suggestionSortKey;
      const direction = this.suggestionSortDirection;
      rows = [...rows].sort((a, b) => {
        const aVal = a[key] == null ? '' : a[key];
        const bVal = b[key] == null ? '' : b[key];
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return (aVal - bVal) * direction;
        }
        return String(aVal).localeCompare(String(bVal), undefined, { numeric: true }) * direction;
      });
    }

    return rows;
  }

  // Select all/none acts on the VISIBLE (filtered) rows so a filtered "select
  // all" can't silently include hidden items.
  toggleAllSuggestions(value) {
    for (const suggestion of this.displaySuggestions()) {
      suggestion.include = value;
    }
  }

  selectedSuggestions() {
    return (this.suggestions || []).filter((suggestion) => suggestion.include && suggestion.qty > 0);
  }

  // Ctrl/Cmd+P while the Min/Max preview is open prints the table (the browser
  // would otherwise print the transfer page behind the modal). Other steps are
  // left to the browser.
  @HostListener('document:keydown', ['$event'])
  onKeydownPrint(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey
      && (event.key === 'p' || event.key === 'P') && this.step === 'replenishPreview') {
      event.preventDefault();
      this.printSuggestions();
    }
  }

  // ---- Print the Min/Max table ----
  // Prints the rows as shown (current filter + sort, unchecked rows greyed with
  // an empty box) with the transfer's identity and the strategy settings in the
  // header, so the sheet can be walked and checked off on the floor. Pure
  // client-side: a popup gets the document and the browser's print dialog.
  printSuggestions() {
    const rows = this.displaySuggestions();
    if (!rows || rows.length === 0) {
      this.toastError('Nothing to print.');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.toastError('The browser blocked the print window. Allow popups for this site and try again.');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(this.buildSuggestionsPrintHtml(rows));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      try {
        printWindow.print();
      } catch (e) {
        console.log('Print error: ', e);
      }
    }, 500);
  }

  // The printout mirrors the transfer's own default (Ctrl+P) print: the status-
  // colored header band with the key and Transfer Type / Source / Target, then
  // a bordered 24px-row grid with Item # + Item first and right-aligned numeric
  // columns, lines/qty totals and a status footer line.
  buildSuggestionsPrintHtml(rows: any[]): string {
    const esc = (value: any) => value === null || value === undefined ? '' : String(value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const num = (value: any) => value === null || value === undefined || value === '' ? '' : Number(value).toLocaleString();
    const purchase = this.purchase || {};
    const typeObject = purchase.typeObject || {};
    const typeName = typeObject.name || 'Transfer';
    const step = Array.isArray(typeObject.steps) ? typeObject.steps.find((candidate) => candidate.key === purchase.status) : null;
    // The transfer header tints the step color to 22% (currentStepColor); match it.
    const stepHex = /^#([0-9a-f]{6})$/i.exec((step && step.color) || '');
    const bandColor = stepHex
      ? 'rgba(' + [0, 2, 4].map((offset) => parseInt(stepHex[1].substr(offset, 2), 16)).join(', ') + ', 0.22)'
      : 'rgba(26, 74, 94, 0.22)';
    const showItemNumber = rows.some((suggestion) => suggestion.itemNumber);
    // Item # gets ONE fixed pixel width for header and every row, sized to the
    // longest value (10px digits are ~5.6px wide; 6.2px per character leaves
    // room for letters), so the grid stays aligned and the number is never
    // clipped; the Item name column absorbs the difference.
    const itemNumberChars = rows.reduce((longest, suggestion) => Math.max(longest, String(suggestion.itemNumber || '').length), 6);
    const itemNumberWidth = Math.ceil(itemNumberChars * 6.2 + 8) + 'px';
    // Group columns print only when some row has a value — a blank column
    // just steals width from the name.
    const showCategory = rows.some((suggestion) => suggestion.categoryName);
    const showSubcategory = rows.some((suggestion) => suggestion.subcategoryName);
    const showSource = this.hasSourceColumn();
    const qtyHeader = this.qtyLabel() + (this.hasCaseColumn() ? ' (cases)' : '');
    const strategy = this.strategy === 'stock-targets'
      ? 'Stock targets (Min/Max)'
      : 'Days of supply · ' + this.windowDays + '-day window · ' + this.targetDays + ' days';
    const scope = (this.replenishScope || []).map((entry) => entry.name).filter((name) => !!name).join(', ');
    const selected = this.selectedSuggestions();
    const selectedQty = this.selectedSuggestionQty();
    const filter = (this.suggestionFilter || '').trim();

    // Header band: the transfer's own detail blocks plus the replenish settings.
    const detail = [
      ['Transfer Type', typeName],
      typeObject.sourceType ? ['Source', purchase.locationName] : null,
      typeObject.targetType ? ['Target', purchase.customerName] : null,
      ['Strategy', strategy],
      ['Scope', scope || 'All items'],
      filter ? ['Filter', filter] : null,
    ].filter((entry) => entry && entry[1])
      .map((entry) => '<div><div class="label">' + esc(entry[0]) + '</div><div class="value">' + esc(entry[1]) + '</div></div>').join('');

    type Column = { label: string; cls: string; cell: (suggestion: any) => string };
    // No checkbox column on paper (Belmont 2026-10-14): unselected rows are
    // still greyed so the sheet matches the screen.
    const columns: Column[] = [
      ...(showItemNumber ? [{ label: 'Item #', cls: 'item-number', cell: (suggestion) => esc(suggestion.itemNumber) }] : []),
      { label: 'Item', cls: 'print-name', cell: (suggestion) => {
        const identifier = this.itemNumberPrimary() ? null : suggestion.sku;
        const units = [suggestion.unitOfMeasure ? '(' + suggestion.unitOfMeasure + ')' : '', suggestion.unitDescription, suggestion.attr]
          .filter((part) => !!part).join(' ') + (suggestion.caseQty > 1 ? ' · ×' + suggestion.caseQty + '/case' : '');
        return esc(suggestion.name) + (units ? ' ' + esc(units) : '') + (identifier ? ' <span class="muted">' + esc(identifier) + '</span>' : '');
      } },
      ...(showCategory ? [{ label: 'Category', cls: 'text', cell: (suggestion) => esc(suggestion.categoryName) }] : []),
      ...(showSubcategory ? [{ label: 'Subcategory', cls: 'text', cell: (suggestion) => esc(suggestion.subcategoryName) }] : []),
      { label: 'Avg/day', cls: 'num', cell: (suggestion) => esc(suggestion.avgDailySales) },
      { label: 'On hand', cls: 'num', cell: (suggestion) => num(suggestion.onHandTarget) },
      { label: 'In transit', cls: 'num', cell: (suggestion) => num(suggestion.inTransit) },
      ...(showSource ? [{ label: 'WH OH Qty', cls: 'num', cell: (suggestion) => num(suggestion.onHandSource) }] : []),
      { label: qtyHeader, cls: 'num qty', cell: (suggestion) => num(suggestion.qty) },
      { label: 'Flags', cls: 'flags', cell: (suggestion) => this.suggestionFlagLabels(suggestion).map((label) => esc(label)).join('; ') },
    ];
    const headerHtml = columns.map((column) => '<div class="' + column.cls + '">' + esc(column.label) + '</div>').join('');
    const rowHtml = rows.map((suggestion) => '<div class="row' + (suggestion.include ? '' : ' excluded') + '">'
      + columns.map((column) => '<div class="' + column.cls + '">' + column.cell(suggestion) + '</div>').join('') + '</div>').join('\n');

    return '<!doctype html><html><head><meta charset="utf-8"><title>Min/Max ' + esc(purchase.key || '') + '</title><style>'
      + '@page { margin: 0.5in; }'
      + 'html, body { margin: 0; padding: 0; background: #fff; }'
      + 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #000; font-size: 11px;'
      + ' -webkit-print-color-adjust: exact; print-color-adjust: exact; }'
      + '.headers { display: flex; align-items: flex-start; padding: 8px 8px 6px; background: ' + esc(bandColor) + '; color: #000; }'
      + '.headers .key { font-size: 20px; margin-right: 24px; line-height: 24px; }'
      + '.headers .purchase-detail { display: flex; flex-wrap: wrap; gap: 4px 24px; }'
      + '.headers .label { font-size: 12px; line-height: 16px; } .headers .value { font-size: 10px; line-height: 12px; }'
      // Column budget fits a portrait Letter page (7.5in printable = 720px): the
      // fixed columns total ~580px so the Item column keeps at least 120px and
      // grows on wider paper; only Flags may shrink.
      // The header mirrors the rows' 1px borders (transparent) so its flexible
      // Item column is exactly as wide as theirs.
      + '.items-header-print { display: flex; border-top: 3px solid #000; border-right: 1px solid transparent;'
      + ' font-weight: 700; font-size: 9px; }'
      + '.items-header-print > div { box-sizing: border-box; border-left: 1px solid transparent; flex: 0 0 56px; line-height: 12px; height: 24px; padding: 0 3px; overflow: hidden; white-space: normal; display: flex; align-items: flex-end; }'
      + '.inventory-table { border-top: 2px solid #000; }'
      + '.inventory-table .row { display: flex; height: 24px; line-height: 24px; border-right: 1px solid #ccc; border-bottom: 1px solid #ccc; page-break-inside: avoid; }'
      + '.inventory-table .row > div { box-sizing: border-box; border-left: 1px solid #ccc; flex: 0 0 56px; line-height: 24px; height: 24px; padding: 0 3px; overflow: hidden; white-space: nowrap; font-size: 10px; }'
      // The Item cell may wrap to two 12px lines inside the 24px row so long
      // names survive a narrow page.
      // The name takes whatever width the fixed columns leave and is clipped
      // (two 12px lines, then cut) rather than ever pushing a column over.
      + '.items-header-print > .print-name, .inventory-table .row > .print-name { flex: 1 1 0; min-width: 0; }'
      + '.inventory-table .row > .print-name { white-space: normal; line-height: 12px; padding-top: 0; }'
      // Item # is the number the floor walks the aisle with: fixed width for
      // every row, wide enough for the longest value, never clipped.
      + '.items-header-print > .item-number, .inventory-table .row > .item-number'
      + ' { flex: 0 0 ' + itemNumberWidth + '; overflow: visible; white-space: nowrap; }'
      + '.items-header-print > .text, .inventory-table .row > .text { flex: 0 1 60px; min-width: 40px; text-overflow: ellipsis; }'
      + '.items-header-print > .num, .inventory-table .row > .num { flex: 0 0 46px; text-align: right; justify-content: flex-end; }'
      + '.items-header-print > .qty, .inventory-table .row > .qty { flex: 0 0 54px; } .inventory-table .row > .qty { font-weight: 700; font-size: 11px; }'
      + '.items-header-print > .flags, .inventory-table .row > .flags { flex: 0 1 110px; min-width: 50px; font-size: 8px; text-overflow: ellipsis; }'
      + '.muted { color: #666; }'
      + '.row.excluded > div { color: #999; } .row.excluded > .qty { font-weight: 400; }'
      + '.totals { display: flex; justify-content: flex-end; gap: 24px; padding: 8px 8px 6px; border-bottom: 1px solid #ccc; }'
      + '.totals .label { font-size: 9px; color: #555; } .totals .value { font-size: 14px; font-weight: 700; }'
      + '.status-line { display: flex; justify-content: space-between; font-size: 9px; padding: 4px 8px; color: #333; text-transform: uppercase; }'
      + '</style></head><body>'
      + '<div class="headers"><div class="key">' + esc(purchase.key || '') + '</div><div class="purchase-detail">' + detail + '</div></div>'
      + '<div class="items-header-print">' + headerHtml + '</div>'
      + '<div class="inventory-table">' + rowHtml + '</div>'
      + '<div class="totals">'
      + '<div><div class="label">Rows:</div><div class="value">' + rows.length.toLocaleString() + '</div></div>'
      + '<div><div class="label">Selected:</div><div class="value">' + selected.length.toLocaleString() + '</div></div>'
      + '<div><div class="label">Qty:</div><div class="value">' + selectedQty.toLocaleString() + '</div></div>'
      + '</div>'
      + '<div class="status-line"><div>Min/Max Replenishment · ' + esc(String(purchase.statusName || purchase.status || '')) + '</div>'
      + '<div>Printed ' + esc(new Date().toLocaleString()) + '</div></div>'
      + '</body></html>';
  }

  selectedSuggestionQty() {
    return this.selectedSuggestions().reduce((total, suggestion) => total + Number(suggestion.qty || 0), 0);
  }

  suggestionFlagLabels(suggestion) {
    const labels = [];
    if (suggestion.flags?.shortfall) { labels.push('Capped — source short (need ' + suggestion.needUnits + ')'); }
    if (suggestion.flags?.increment) { labels.push('Rounded to increment of ' + suggestion.orderIncrement); }
    if (suggestion.flags?.lowHistory) { labels.push('Low history'); }
    if (suggestion.flags?.negativeOnHand) { labels.push('Negative on hand'); }
    if (suggestion.flags?.notCarriedAtTarget) { labels.push('Not carried at target'); }
    return labels;
  }

  async commitReplenish() {
    const rows = this.selectedSuggestions().map((suggestion) => ({
      accountId: suggestion.accountId,
      qty: Math.round(Number(suggestion.qty)),
    }));
    if (rows.length === 0) {
      return;
    }
    this.busy = true;
    this.busyMessage = 'Adding ' + rows.length + ' items…';
    try {
      const commitResult: any = await this.ioService.post('/transfer/commitReplenishItems', {
        purchaseId: this.purchase._id,
        strategy: this.strategy,
        parameters: { windowDays: this.windowDays, targetDays: this.targetDays },
        items: rows,
      });
      this.busy = false;

      let summary = 'Added ' + (commitResult.added || 0) + ' item' + (commitResult.added === 1 ? '' : 's');
      if (commitResult.skipped?.length) {
        summary += ' · ' + commitResult.skipped.length + ' skipped';
      }
      summary += '.';
      this.finish(summary);
    } catch (e) {
      this.busy = false;
      this.toastError((e as any)?.error?.msg || 'Commit failed.');
    }
  }

  // ==== Add by Filter branch =========================================

  async addByFilter() {
    this.busy = true;
    this.busyMessage = 'Adding items…';
    try {
      const filterInput: any = { purchaseId: this.purchase._id };
      if (this.selectedCategory && this.selectedCategory._id) {
        if (this.selectedCategory.subtype === 'group') {
          filterInput.groupId = this.selectedCategory._id;
        } else {
          filterInput.categoryId = this.selectedCategory._id;
        }
      }
      const addResult: any = await this.ioService.post('/transfer/addAllInventory', filterInput);
      this.busy = false;
      this.finish('Added ' + (addResult.added || 0) + ' item' + (addResult.added === 1 ? '' : 's') + '.');
    } catch (e) {
      this.busy = false;
      this.toastError((e as any)?.error?.msg || 'Add failed.');
    }
  }

}
